import React, { useState } from 'react';

const ESTIMATE_API_SETUP_VIDEO =
  'https://www.youtube.com/watch?v=Lj43aSwNpog&t=139s';

const MAIN_TABS = [
  { id: 'integration', label: 'Integration settings' },
  { id: 'estimate', label: 'Estimate & completion' },
  { id: 'form', label: 'Form content' }
];

const FORM_SUBTABS = [
  { id: 'q1', label: 'Question 1' },
  { id: 'q2', label: 'Question 2' },
  { id: 'q7', label: 'Question 7' },
  { id: 'q9', label: 'Question 9' },
  { id: 'routing', label: 'Questions 3–6 routing' },
  { id: 'q8', label: 'Question 8 (photos)' },
  { id: 'q10', label: 'Question 10 (unlock)' }
];

/** Routes follow Question 1's options; anything unmatched still gets listed after them. */
function routeOrderFor(config) {
  const routing = config?.routing || {};
  const fromQ1 = (config?.question1?.options || [])
    .map((o) => o.routeKey)
    .filter((k) => k && routing[k]);
  const rest = Object.keys(routing).filter((k) => !fromQ1.includes(k));
  return [...fromQ1, ...rest];
}

const READY_FIELDS = [
  ['estimateTitle', 'Estimate Title', false],
  ['estimateSubtitle', 'Estimate Subtitle', true],
  ['reimaginedSectionTitle', 'Reimagined Section Title', false],
  ['yourVisionTitle', 'Your Vision Title', false],
  ['visionSummaryTemplate', 'Vision Summary Template', true],
  ['estimatedInvestmentTitle', 'Estimated Investment Title', false],
  ['locationNoteText', 'Note text (outside service area)', true],
  ['getStartedTitle', 'Get Started Title', false],
  ['getStartedSubtitle', 'Get Started Subtitle', true],
  ['callPhoneDigits', 'Call Phone Number (digits only)', false]
];

function labelClass(darkMode) {
  return `block text-sm font-medium mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`;
}

function fieldWrap(darkMode) {
  return `w-full rounded-lg border px-3 py-2 text-sm cursor-default ${
    darkMode
      ? 'bg-gray-900/80 border-gray-600 text-gray-100'
      : 'bg-gray-50 border-gray-200 text-gray-900'
  }`;
}

function RoField({ label, value, darkMode, multiline = false, rows = 3 }) {
  const v = value != null ? String(value) : '';
  return (
    <div className="mb-3">
      <span className={labelClass(darkMode)}>{label}</span>
      {multiline ? (
        <textarea readOnly rows={rows} value={v} className={fieldWrap(darkMode)} />
      ) : (
        <input readOnly value={v} className={fieldWrap(darkMode)} />
      )}
    </div>
  );
}

function tabBtnClass(active, darkMode) {
  return `flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
    active
      ? darkMode
        ? 'bg-gray-700 text-white'
        : 'bg-white text-gray-900 shadow'
      : darkMode
        ? 'text-gray-400 hover:text-white'
        : 'text-gray-600 hover:text-gray-900'
  }`;
}

/**
 * Read-only mirror of Website Calculators tabs for CRM fulfillment review.
 */
export default function EstimateCalculatorBackupViewer({ config, loading, darkMode }) {
  const [mainTab, setMainTab] = useState('integration');
  const [formTab, setFormTab] = useState('q1');

  if (loading) {
    return (
      <div
        className={`mt-8 rounded-lg shadow-md p-8 text-center ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600'}`}
      >
        Loading saved calculator configuration…
      </div>
    );
  }

  if (!config) {
    return (
      <div
        className={`mt-8 rounded-lg shadow-md p-8 text-center ${darkMode ? 'bg-gray-800 text-amber-200' : 'bg-white text-amber-900'}`}
      >
        No merged calculator configuration available.
      </div>
    );
  }

  const settings = config.settings || {};
  const erp = config.estimateReadyPage || {};
  const scheduleLink =
    erp.scheduleConsultationLink ?? erp.scheduleButtonText ?? '';

  const hasApiKey = Boolean(String(settings.apiKey || '').trim());

  return (
    <div
      className={`mt-8 rounded-lg shadow-md p-6 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}
    >
      <h2 className={`text-xl font-semibold mb-1 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        Calculator Inputs
      </h2>
      <p className={`text-sm mb-6 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        Read-only mirror of the client portal: Integration settings, Estimate & completion, and Form content. Use with the
        downloadable JSON above.
      </p>

      <div className={`flex flex-wrap rounded-lg p-1 mb-6 gap-1 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
        {MAIN_TABS.map((t) => (
          <button key={t.id} type="button" className={tabBtnClass(mainTab === t.id, darkMode)} onClick={() => setMainTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {mainTab === 'integration' && (
        <div className="space-y-6">
          <section>
            <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>API key</h3>
            <RoField label="API key" value={hasApiKey ? '•••••••••••••••••••• (stored)' : '(empty)'} darkMode={darkMode} />
            <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <span className="font-medium">Resource:</span>{' '}
              <a
                href={ESTIMATE_API_SETUP_VIDEO}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:underline dark:text-purple-400"
              >
                How to get your API key (YouTube)
              </a>
            </p>
          </section>

          <section className={`pt-4 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Webhook</h3>
            <div className="mb-3 flex items-center gap-2">
              <input disabled type="checkbox" checked={!!settings.webhookNotApplicable} className="rounded" />
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Webhook URL not applicable (N/A)</span>
            </div>
            {!settings.webhookNotApplicable && (
              <RoField label="Webhook URL (optional)" value={settings.webhookUrl || ''} darkMode={darkMode} />
            )}
          </section>
        </div>
      )}

      {mainTab === 'estimate' && (
        <div className="space-y-8">
          <section>
            <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Estimate ± range</h3>
            <RoField label="Estimate ± range (%)" value={String(config.estimateRangePercent ?? '')} darkMode={darkMode} />
          </section>

          <section className={`pt-4 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <h3 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Estimate ready page</h3>
            <p className={`text-xs mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Placeholders: {'{'}project_type{'}'}, {'{'}timeline{'}'}, {'{'}location{'}'}
            </p>
            {READY_FIELDS.map(([field, lab, multiline]) => (
              <RoField
                key={field}
                label={lab}
                value={erp[field] ?? ''}
                darkMode={darkMode}
                multiline={multiline}
                rows={multiline ? 2 : 3}
              />
            ))}
            <RoField
              label="Preliminary Estimate Note"
              value={erp.preliminaryEstimateNote ?? ''}
              darkMode={darkMode}
              multiline
              rows={6}
            />
            <RoField label="Schedule Consultation Link" value={scheduleLink} darkMode={darkMode} />
            <div className="mb-3 flex items-start gap-3">
              <input disabled type="checkbox" checked={!!erp.locationNoteEnabled} className="mt-1 rounded" />
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Show location note when user is outside service area
              </span>
            </div>
            <RoField
              label="Zip codes / cities within range (one per row)"
              value={erp.locationInRangeList ?? ''}
              darkMode={darkMode}
              multiline
              rows={5}
            />
          </section>

          <section className={`pt-4 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Estimate formulas (per project type)</h3>
            <p className={`text-xs mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
              Operators apply across Q3→Q6 multiplier factors in order (× + − ÷).
            </p>
            {(config.estimateFormulas && Object.keys(config.estimateFormulas).length > 0
              ? Object.entries(config.estimateFormulas)
              : []
            ).map(([key, entry]) => (
              <div
                key={key}
                className={`mb-4 p-4 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}
              >
                <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{entry?.label || key}</p>
                <RoField label="Routing ref (question text)" value={entry?.routingRef ?? ''} darkMode={darkMode} multiline rows={2} />
                <RoField label="Operators (4)" value={(entry?.operators || []).join(' ')} darkMode={darkMode} />
              </div>
            ))}
          </section>
        </div>
      )}

      {mainTab === 'form' && (
        <div>
          <div className={`flex flex-wrap rounded-lg p-1 mb-4 gap-1 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
            {FORM_SUBTABS.map((t) => (
              <button key={t.id} type="button" className={tabBtnClass(formTab === t.id, darkMode)} onClick={() => setFormTab(t.id)}>
                {t.label}
              </button>
            ))}
          </div>

          {formTab === 'q1' && (
            <div>
              <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Question 1 — Project type</h3>
              <RoField label="Header Text" value={config.question1?.headerText ?? ''} darkMode={darkMode} />
              <p className={`text-sm font-medium mb-2 mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Options</p>
              {(config.question1?.options || []).map((opt, i) => (
                <div key={opt.id || i} className="flex flex-wrap gap-2 mb-2 items-end">
                  <input readOnly value={opt.label ?? ''} className={`${fieldWrap(darkMode)} flex-1 min-w-[120px]`} />
                  <input readOnly value={String(opt.baseCost ?? '')} className={`${fieldWrap(darkMode)} w-28 shrink-0`} />
                </div>
              ))}
            </div>
          )}

          {formTab === 'q2' && (
            <div>
              <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Question 2 — Property type & rejection</h3>
              <RoField label="Header Text" value={config.question2?.headerText ?? ''} darkMode={darkMode} />
              <p className={`font-medium mb-2 mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Options</p>
              {(config.question2?.options || []).map((opt, i) => (
                <div key={opt.id || i} className="flex flex-wrap gap-2 mb-2 items-center">
                  <input readOnly value={opt.label ?? ''} className={`${fieldWrap(darkMode)} flex-1`} />
                  <label className="text-xs whitespace-nowrap flex items-center gap-1">
                    <input disabled type="checkbox" checked={!!opt.reject} /> Reject
                  </label>
                </div>
              ))}
              <h4 className={`font-semibold mb-2 mt-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Rejection screen</h4>
              {['header', 'subheader', 'referralHeader'].map((k) => (
                <RoField
                  key={k}
                  label={k}
                  value={config.question2?.rejection?.[k] ?? ''}
                  darkMode={darkMode}
                  multiline={k === 'subheader'}
                  rows={3}
                />
              ))}
              <p className={`font-medium mt-4 mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Referrals</p>
              {(config.question2?.rejection?.referrals || []).map((ref, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input readOnly value={ref.label ?? ''} className={fieldWrap(darkMode)} placeholder="Label" />
                  <input readOnly value={ref.url ?? ''} className={fieldWrap(darkMode)} placeholder="URL" />
                </div>
              ))}
              <RoField label="Contact Us Anyway button link" value={config.question2?.rejection?.contactUsAnywayLink ?? ''} darkMode={darkMode} />
              <RoField label="Get in touch link" value={config.question2?.rejection?.getInTouchLink ?? ''} darkMode={darkMode} />
            </div>
          )}

          {formTab === 'q7' && (
            <div>
              <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Question 7 — Timeline</h3>
              <RoField label="Header Text" value={config.question7?.headerText ?? ''} darkMode={darkMode} />
              {(config.question7?.options || []).map((opt, i) => (
                <div key={opt.id || i} className="mb-2">
                  <input readOnly value={opt.label ?? ''} className={fieldWrap(darkMode)} />
                </div>
              ))}
            </div>
          )}

          {formTab === 'q9' && (
            <div>
              <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Question 9 — Location</h3>
              <RoField label="Header Text" value={config.question9?.headerText ?? ''} darkMode={darkMode} />
              <RoField label="Placeholder Text" value={config.question9?.placeholderText ?? ''} darkMode={darkMode} />
            </div>
          )}

          {formTab === 'routing' && (
            <div className="space-y-8">
              <h3 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Questions 3–6 routing</h3>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Each route has four questions (Q3–Q6). Option values are numeric multipliers.
              </p>
              {routeOrderFor(config).map((rk) => {
                const bundle = config.routing?.[rk];
                if (!bundle) return null;
                return (
                  <section
                    key={rk}
                    className={`pb-6 mb-6 border-b last:border-0 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
                  >
                    <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Route: {bundle.routeLabel || rk}
                    </h4>
                    {(bundle.questions || []).map((q) => (
                      <div key={q.num} className="mb-8 last:mb-0">
                        <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Question {q.num}
                        </p>
                        <RoField label="Question Text" value={q.questionText ?? ''} darkMode={darkMode} multiline rows={2} />
                        <p className={`text-sm font-medium mb-1 mt-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Options</p>
                        <div className="flex gap-2 mb-2 items-end">
                          <div className="flex-1 min-w-[160px]" />
                          <div className="w-24 shrink-0 text-center">
                            <span className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Multiplier</span>
                          </div>
                        </div>
                        {(q.options || []).map((opt, oi) => (
                          <div key={oi} className="flex flex-wrap gap-2 mb-2 items-end">
                            <input readOnly value={opt.label ?? ''} className={`${fieldWrap(darkMode)} flex-1 min-w-[160px]`} />
                            <input readOnly value={String(opt.value ?? '')} className={`${fieldWrap(darkMode)} w-24 shrink-0`} />
                          </div>
                        ))}
                      </div>
                    ))}
                  </section>
                );
              })}
            </div>
          )}

          {formTab === 'q8' && (
            <div>
              <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Question 8 — Photo upload</h3>
              <RoField label="Header Text" value={config.question8?.headerText ?? ''} darkMode={darkMode} />
              <RoField label="Image Disclaimer" value={config.question8?.imageDisclaimerText ?? ''} darkMode={darkMode} multiline rows={2} />
            </div>
          )}

          {formTab === 'q10' && (
            <div>
              <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Question 10 — Unlock form</h3>
              <RoField label="Title" value={config.question10?.title ?? ''} darkMode={darkMode} />
              <RoField label="Button Text" value={config.question10?.buttonText ?? ''} darkMode={darkMode} />
              <p className={`font-medium mb-2 mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Form Fields</p>
              {(config.question10?.fields || []).map((f, i) => (
                <div key={f.id || i} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2 items-end">
                  <input readOnly value={f.label ?? ''} className={fieldWrap(darkMode)} />
                  <input readOnly value={f.placeholder ?? ''} className={fieldWrap(darkMode)} />
                  <label className="text-xs whitespace-nowrap flex items-center gap-1">
                    <input disabled type="checkbox" checked={!!f.required} /> Required
                  </label>
                </div>
              ))}
              <div className="mb-2 mt-4 flex items-center gap-2">
                <input disabled type="checkbox" checked={!!config.question10?.contentCheckboxEnabled} />
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Enable content checkbox</span>
              </div>
              <RoField label="Content checkbox text" value={config.question10?.contentCheckboxText ?? ''} darkMode={darkMode} multiline rows={2} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
